import React from 'react';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetFooter,
    SheetClose,
} from "./ui/sheet";
import { Button } from './ui/button';
import {
    CheckCircle2,
    AlertCircle,
    ArrowRight,
    Search,
    AlertTriangle,
    FileCheck2
} from 'lucide-react';
import { cn } from '../lib/utils';

export default function ImportSummaryModal({ open, onOpenChange, results, onConfirm }) {
    if (!results) return null;

    const {
        created = 0,
        matched = 0,
        updated = 0,
        skipped = 0,
        conflicts = 0,
        warnings = [],
        insight = null,
        repairHints = []
    } = results;

    const stats = [
        { label: 'Created', value: created, icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
        { label: 'Matched', value: matched, icon: Search, color: 'text-blue-600', bg: 'bg-blue-50' },
        { label: 'Updated', value: updated, icon: ArrowRight, color: 'text-indigo-600', bg: 'bg-indigo-50' },
        { label: 'Conflicts', value: conflicts, icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50' },
        { label: 'Skipped', value: skipped, icon: AlertCircle, color: 'text-gray-600', bg: 'bg-gray-50' },
    ];

    const getSeverityStyles = (severity) => {
        switch (severity) {
            case 'high': return 'bg-red-50 border-red-100 text-red-800';
            case 'medium': return 'bg-amber-50 border-amber-100 text-amber-800';
            default: return 'bg-blue-50 border-blue-100 text-blue-800';
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="max-w-md max-h-[90vh] overflow-y-auto">
                <SheetHeader>
                    <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 bg-violet-100 text-violet-600 rounded-lg">
                            <FileCheck2 className="h-5 w-5" />
                        </div>
                        <SheetTitle>Import Summary</SheetTitle>
                    </div>
                </SheetHeader>

                <div className="py-2 space-y-4">
                    {/* Insights Block */}
                    {insight && (
                        <div className="p-3 rounded-lg border border-violet-100 bg-violet-50/50 flex flex-col gap-1 shadow-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-violet-600">Pattern Insight</span>
                                <Badge variant="secondary" className="bg-violet-100 text-violet-700 hover:bg-violet-100 ring-1 ring-violet-200">{insight.topIssue}</Badge>
                            </div>
                            <p className="text-sm font-semibold text-foreground mt-1">{insight.count} records affected</p>
                            <p className="text-xs text-muted-foreground leading-relaxed italic">"{insight.recommendation}"</p>
                        </div>
                    )}

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                        {stats.filter(s => s.value > 0 || (s.label === 'Created' && created === 0)).map((s) => (
                            <div key={s.label} className={cn("p-2.5 rounded-lg border flex flex-col gap-0.5", s.bg)}>
                                <div className="flex items-center justify-between">
                                    <s.icon className={cn("h-3 w-3", s.color)} />
                                    <span className="text-lg font-bold">{s.value}</span>
                                </div>
                                <span className="text-[10px] font-medium text-muted-foreground uppercase">{s.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Repair Guidance */}
                    {repairHints.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-1">Repair Guidance</h4>
                            {repairHints.map((hint, i) => (
                                <div key={i} className={cn("p-3 border rounded-lg flex gap-3", getSeverityStyles(hint.severity))}>
                                    <div className="mt-0.5">
                                        <AlertTriangle className="h-4 w-4 shrink-0" />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-xs font-bold leading-none">{hint.title}</p>
                                        <p className="text-xs leading-normal opacity-90">{hint.advice}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!insight && warnings.length === 0 && (
                        <div className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg border border-dashed text-center">
                            <p>All records matched the canonical safety model. No quality issues detected.</p>
                        </div>
                    )}
                </div>

                <SheetFooter className="sm:justify-start">
                    <SheetClose asChild>
                        <Button className="w-full bg-violet-600 hover:bg-violet-700">
                            Continue to Dashboard
                        </Button>
                    </SheetClose>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}

// Helper Badge component if not imported
const Badge = ({ children, className, variant = 'default' }) => (
    <span className={cn(
        "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium",
        variant === 'secondary' ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground",
        className
    )}>
        {children}
    </span>
);
